import { FulfillmentStatus, OrderLineItemDto } from "@lib/ShiOneClient";
import { OrderRow } from "@features/orders-features/types/types";
import { DateTime } from "luxon";
import { parseOrderDate } from "@features/orders-features/functions/formatOrderDate";

export interface OrderProgressStepViewModel {
  key: "orderPlaced" | "processing" | "shipped" | "delivered";
  label: string;
  dateText?: string;
  completed: boolean;
  active: boolean;
}

export interface OrderProgressTrackerViewModel {
  steps: OrderProgressStepViewModel[];
  deliveredAuxText?: string;
}

const ELECTRONIC_PRODUCT_TYPES = new Set(["SOFTWARE", "SERVICE", "FEES"]);

function normalizeStatus(value: string | undefined): FulfillmentStatus | null {
  const status = (value ?? "").replace(/\s+/g, "").toLowerCase();

  if (status === "orderplaced") return FulfillmentStatus.OrderPlaced;
  if (status === "processing") return FulfillmentStatus.Processing;
  if (status === "partiallyshipped") return FulfillmentStatus.PartiallyShipped;
  if (status === "shipped") return FulfillmentStatus.Shipped;
  if (status === "delivered") return FulfillmentStatus.Delivered;

  return null;
}

function formatDateNoLeadingZeros(value: DateTime | null | undefined): string | undefined {
  if (!value?.isValid) return undefined;
  return value.toFormat("M/d/yyyy");
}

function parseGroupProductType(groupKey: string): string | null {
  const match = groupKey.match(/(HARDWARE|SOFTWARE|SERVICE|OTHER|FEES)/i);
  return match?.[1]?.toUpperCase() ?? null;
}

function normalizeItemGroupId(itemGroupId?: string): string {
  return (itemGroupId ?? "").trim().toLowerCase().replace(/\s+/g, "");
}

function parseItemGroupIdProductType(itemGroupId?: string): string | null {
  const value = normalizeItemGroupId(itemGroupId);
  if (!value) return null;

  if (value === "hardware") return "HARDWARE";
  if (value === "software" || value === "esa") return "SOFTWARE";

  if (
    [
      "sercloud",
      "serconfig",
      "serconsult",
      "serltm",
      "sersam",
      "service",
      "cloud-mpc",
      "sercost",
      "sertravel",
      "strata",
      "ssl-conslt",
      "ssl-cost",
      "ssl-item",
    ].includes(value)
  ) {
    return "SERVICE";
  }

  if (["deliverchg", "serfee"].includes(value)) return "FEES";

  return "OTHER";
}

function isCanceledLine(item: OrderLineItemDto): boolean {
  const lineCanceled = (item.lineCanceled ?? "").toLowerCase();
  if (lineCanceled === "true") return true;

  const lineStatus = (item.lineStatus ?? "").toLowerCase();
  const status = (item.status ?? "").toLowerCase();

  return lineStatus.includes("cancel") || status.includes("cancel");
}

function isDeliveredLine(item: OrderLineItemDto): boolean {
  const lineStatus = (item.lineStatus ?? "").toLowerCase();
  const status = (item.status ?? "").toLowerCase();

  return (
    item.deliveryOn != null ||
    lineStatus.includes("delivered") ||
    lineStatus.includes("invoiced") ||
    status.includes("delivered") ||
    status.includes("invoiced")
  );
}

function isShippedLine(item: OrderLineItemDto): boolean {
  return (
    !!item.trackingNumber ||
    (item.qtyShipped ?? 0) > 0 ||
    item.shippedOn != null ||
    isDeliveredLine(item)
  );
}

function buildLineItems(order: OrderRow): Array<{ item: OrderLineItemDto; productType: string | null }> {
  const groups = (order.orderLineProducts ?? {}) as Record<string, OrderLineItemDto[]>;
  const grouped = Object.entries(groups).flatMap(([groupKey, items]) => {
    const groupProductType = parseGroupProductType(groupKey);

    return items.map((item) => ({
      item,
      productType: groupProductType ?? parseItemGroupIdProductType(item.itemGroupId),
    }));
  });

  if (grouped.length > 0) {
    return grouped;
  }

  return (order.allLineItems ?? []).map((item) => ({
    item,
    productType: parseItemGroupIdProductType(item.itemGroupId),
  }));
}

export function buildOrderProgressTracker(order: OrderRow): OrderProgressTrackerViewModel {
  const placedOn = parseOrderDate(order.orderDate);
  const placedDate = formatDateNoLeadingZeros(placedOn);
  const processingDate = placedDate;

  const normalizedStatus = normalizeStatus(order.status);
  const lineItems = buildLineItems(order);

  let hasHardware = false;
  let shippableLineCount = 0;
  let shippedLineCount = 0;

  const deliveredEtaCandidates: DateTime[] = [];
  const shippedOnCandidates: DateTime[] = [];

  for (const { item, productType } of lineItems) {
    if (isCanceledLine(item)) {
      continue;
    }

    const resolvedType = productType ?? "OTHER";
    const isHardware = !ELECTRONIC_PRODUCT_TYPES.has(resolvedType);

    if (isHardware) {
      hasHardware = true;
      shippableLineCount += 1;

      if (isShippedLine(item)) {
        shippedLineCount += 1;
      }

      if (item.deliveryOn) {
        const eta = parseOrderDate(item.deliveryOn);
        if (eta) deliveredEtaCandidates.push(eta);
      }

      if (item.shippedOn) {
        const shippedOnDate = parseOrderDate(item.shippedOn);
        if (shippedOnDate) shippedOnCandidates.push(shippedOnDate);
      }
    }
  }

  const allShippableItemsShipped =
    hasHardware && shippableLineCount > 0 && shippedLineCount >= shippableLineCount;

  const effectiveStatus = normalizedStatus;

  const shippedLabel =
    effectiveStatus === FulfillmentStatus.PartiallyShipped
      ? "Partially Shipped"
      : "Shipped";

  const latestEta = deliveredEtaCandidates.length
    ? DateTime.max(...deliveredEtaCandidates)
    : null;
  const latestShippedOn = shippedOnCandidates.length
    ? DateTime.max(...shippedOnCandidates)
    : null;

  const shippedDate =
    shippedLabel === "Partially Shipped"
      ? formatDateNoLeadingZeros(latestShippedOn)
      : formatDateNoLeadingZeros(latestEta ?? latestShippedOn);

  const deliveredDate = formatDateNoLeadingZeros(latestEta);

  const showDeliveredOn =
    hasHardware &&
    allShippableItemsShipped &&
    !!latestEta &&
    effectiveStatus === FulfillmentStatus.Delivered;
  const deliveredAuxText = showDeliveredOn
    ? `Delivered on ${formatDateNoLeadingZeros(latestEta)}`
    : undefined;

  const deliveredCompleted = effectiveStatus === FulfillmentStatus.Delivered;

  const steps: OrderProgressStepViewModel[] = [
    {
      key: "orderPlaced",
      label: "Order Placed",
      dateText: placedDate,
      completed: true,
      active: effectiveStatus === FulfillmentStatus.OrderPlaced,
    },
    {
      key: "processing",
      label: "Processing",
      dateText: processingDate,
      completed: effectiveStatus !== FulfillmentStatus.OrderPlaced,
      active: effectiveStatus === FulfillmentStatus.Processing,
    },
    {
      key: "shipped",
      label: shippedLabel,
      dateText: shippedDate,
      completed: hasHardware
        ? effectiveStatus === FulfillmentStatus.Shipped || deliveredCompleted
        : deliveredCompleted,
      active: hasHardware && (
        effectiveStatus === FulfillmentStatus.PartiallyShipped ||
        effectiveStatus === FulfillmentStatus.Shipped
      ),
    },
    {
      key: "delivered",
      label: "Delivered",
      dateText: deliveredDate,
      completed: deliveredCompleted,
      active: false,
    },
  ];

  return {
    steps,
    deliveredAuxText,
  };
}
